import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth } from '../context/AdminAuthContext';
import toast from 'react-hot-toast';
import {
  PaperAirplaneIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/** Parse JSON (or plain-text) error bodies from failed fetch responses. */
async function readFetchErrorMessage(response) {
  const fallback = `Request failed (${response.status})`;
  try {
    const text = await response.text();
    if (!text.trim()) return fallback;
    try {
      const body = JSON.parse(text);
      if (typeof body?.error === 'string' && body.error) return body.error;
    } catch {
      /* not JSON */
    }
    return text.trim().slice(0, 200);
  } catch {
    return fallback;
  }
}

const WhatsAppBroadcast = () => {
  const { token } = useAdminAuth();
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [senders, setSenders] = useState([]);
  const [loadingSenders, setLoadingSenders] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [fromNumber, setFromNumber] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testNumber, setTestNumber] = useState('');
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState(null);
  const [csvFileName, setCsvFileName] = useState('');
  const [optouts, setOptouts] = useState([]);
  const [optoutInput, setOptoutInput] = useState('');
  const [loadingOptouts, setLoadingOptouts] = useState(true);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/whatsapp-broadcast/templates`);
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load WhatsApp templates');
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  const fetchSenders = useCallback(async () => {
    try {
      setLoadingSenders(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/whatsapp-broadcast/senders`);
      const data = response.data.data || [];
      setSenders(data);
      if (data.length === 1) {
        setFromNumber(data[0].phone);
      }
    } catch (error) {
      console.error('Error fetching senders:', error);
      toast.error('Failed to load sender numbers');
    } finally {
      setLoadingSenders(false);
    }
  }, []);

  const fetchOptouts = useCallback(async () => {
    try {
      setLoadingOptouts(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/whatsapp-broadcast/optouts`);
      setOptouts(response.data.data || []);
    } catch (error) {
      console.error('Error fetching optouts:', error);
    } finally {
      setLoadingOptouts(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    fetchSenders();
    fetchOptouts();
  }, [fetchTemplates, fetchSenders, fetchOptouts]);

  const handleAddOptout = async () => {
    const phone = optoutInput.trim();
    if (!phone) return;
    const normalized = phone.startsWith('+') ? phone : `+${phone}`;
    try {
      await axios.post(`${API_BASE_URL}/api/admin/whatsapp-broadcast/optouts`, { phone: normalized });
      setOptoutInput('');
      toast.success(`${normalized} added to opt-out list`);
      fetchOptouts();
    } catch (error) {
      toast.error('Failed to add number');
    }
  };

  const handleRemoveOptout = async (phone) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/whatsapp-broadcast/optouts/${encodeURIComponent(phone)}`);
      toast.success(`${phone} removed from opt-out list`);
      fetchOptouts();
    } catch (error) {
      toast.error('Failed to remove number');
    }
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split(/\r?\n/).filter(line => line.trim());

      const parsed = [];
      for (const line of lines) {
        const cols = line.split(',').map(c => c.trim().replace(/^['"]|['"]$/g, ''));
        // First column is the phone number
        const raw = cols[0] || '';
        const cleaned = raw.replace(/[\s\-()]/g, '');
        if (!/^\+?\d{7,15}$/.test(cleaned)) continue;
        const phone = cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
        // Remaining columns are template variable values (keyed as "1", "2", ...)
        const vars = {};
        for (let i = 1; i < cols.length; i++) {
          if (cols[i]) vars[String(i)] = cols[i];
        }
        parsed.push({ phone, variables: vars });
      }

      setRecipients(parsed);
      if (parsed.length === 0) {
        toast.error('No valid phone numbers found in CSV');
      } else {
        const varCount = parsed[0] ? Object.keys(parsed[0].variables).length : 0;
        const msg = `Loaded ${parsed.length} recipient${parsed.length > 1 ? 's' : ''}` +
          (varCount > 0 ? ` with ${varCount} variable${varCount > 1 ? 's' : ''} per row` : '');
        toast.success(msg);
      }
    };
    reader.readAsText(file);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setResults(null);
  };

  const handleSend = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }
    if (!fromNumber.trim()) {
      toast.error('Please enter a sender number');
      return;
    }
    if (recipients.length === 0) {
      toast.error('Please upload a CSV with phone numbers');
      return;
    }

    const confirmed = window.confirm(
      `Send template "${selectedTemplate.friendlyName}" to ${recipients.length} recipient${recipients.length > 1 ? 's' : ''}?`
    );
    if (!confirmed) return;

    setSending(true);
    setResults(null);
    setProgress({ total: recipients.length, sent: 0, failed: 0, skipped: 0, current: 0 });

    const normalizedFrom = fromNumber.trim().startsWith('+')
      ? fromNumber.trim()
      : `+${fromNumber.trim()}`;

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/whatsapp-broadcast/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          contentSid: selectedTemplate.sid,
          from: normalizedFrom,
          recipients,
        }),
      });

      if (!response.ok) {
        toast.error(await readFetchErrorMessage(response));
        setProgress(null);
        return;
      }

      if (!response.body) {
        toast.error('Broadcast failed: empty response');
        setProgress(null);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let eventType = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.slice(6));
              if (eventType === 'progress') {
                setProgress(data);
              } else if (eventType === 'done') {
                setResults(data);
                setProgress(null);
                if (data.failed === 0 && data.skipped === 0) {
                  toast.success(`All ${data.sent} messages sent successfully!`);
                } else {
                  const parts = [`Sent: ${data.sent}`];
                  if (data.skipped > 0) parts.push(`Skipped (opted-out): ${data.skipped}`);
                  if (data.failed > 0) parts.push(`Failed: ${data.failed}`);
                  toast(parts.join(', '), { icon: '⚠️' });
                }
              } else if (eventType === 'error') {
                toast.error(data.error || 'Broadcast failed');
                setProgress(null);
              }
            } catch { /* ignore malformed JSON */ }
            eventType = null;
          }
        }
      }
    } catch (error) {
      console.error('Broadcast error:', error);
      toast.error('Failed to send broadcast');
      setProgress(null);
    } finally {
      setSending(false);
    }
  };

  const clearRecipients = () => {
    setRecipients([]);
    setCsvFileName('');
    setResults(null);
  };

  const handleSendTest = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }
    if (!fromNumber.trim()) {
      toast.error('Please select a sender number');
      return;
    }
    if (!testNumber.trim()) {
      toast.error('Please enter a test phone number');
      return;
    }

    try {
      setSendingTest(true);
      const normalizedFrom = fromNumber.trim().startsWith('+') ? fromNumber.trim() : `+${fromNumber.trim()}`;
      const normalizedTo = testNumber.trim().startsWith('+') ? testNumber.trim() : `+${testNumber.trim()}`;

      await axios.post(`${API_BASE_URL}/api/admin/whatsapp-broadcast/send-test`, {
        contentSid: selectedTemplate.sid,
        from: normalizedFrom,
        to: normalizedTo,
      });

      toast.success(`Test message sent to ${normalizedTo}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send test');
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">WhatsApp Broadcast</h1>
            <p className="mt-1 text-sm text-gray-500">
              Send a WhatsApp template message to a list of recipients from a CSV file.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: Template selection */}
          <div className="lg:col-span-2 space-y-6">
            {/* Template picker */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <DocumentTextIcon className="h-5 w-5 text-green-600" />
                Select Template
              </h2>

              {loadingTemplates ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
                </div>
              ) : templates.length === 0 ? (
                <p className="text-gray-500 text-sm py-4">
                  No approved templates found in your Twilio account.
                </p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {templates.map(template => (
                    <button
                      key={template.sid}
                      onClick={() => handleTemplateSelect(template)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedTemplate?.sid === template.sid
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">{template.friendlyName}</span>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                          {template.language || 'en'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1 font-mono">{template.sid}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Template variables hint */}
            {selectedTemplate && selectedTemplate.variables && Object.keys(selectedTemplate.variables).length > 0 && (
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-2">Template Variables</h2>
                <p className="text-sm text-gray-600 mb-3">
                  This template expects <strong>{Object.keys(selectedTemplate.variables).length}</strong> variable{Object.keys(selectedTemplate.variables).length > 1 ? 's' : ''}.
                  Include them as extra columns in your CSV after the phone number.
                </p>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <p className="text-xs font-medium text-gray-500 mb-1">CSV format example:</p>
                  <code className="text-xs text-gray-700 font-mono">
                    +972501234567,{Object.keys(selectedTemplate.variables).map((_, i) => `value${i + 1}`).join(',')}
                  </code>
                </div>
                <div className="mt-3 space-y-1">
                  {Object.entries(selectedTemplate.variables).map(([key, val]) => (
                    <div key={key} className="text-xs text-gray-600">
                      <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">{`{{${key}}}`}</span>
                      <span className="ml-2 text-gray-400">→ CSV column {key}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Template content preview */}
            {selectedTemplate && (
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Template Preview</h2>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-semibold text-gray-700">{selectedTemplate.friendlyName}</span>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                      {Object.keys(selectedTemplate.types)[0]?.replace('twilio/', '') || 'text'}
                    </span>
                  </div>
                  {/* Render based on template type */}
                  {Object.entries(selectedTemplate.types).map(([type, content]) => (
                    <div key={type} className="space-y-2">
                      {content.body && (
                        <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100 whitespace-pre-wrap text-sm text-gray-800">
                          {content.body}
                        </div>
                      )}
                      {content.title && (
                        <div className="font-semibold text-gray-900 text-sm">{content.title}</div>
                      )}
                      {content.subtitle && (
                        <div className="text-xs text-gray-500">{content.subtitle}</div>
                      )}
                      {content.media && content.media.length > 0 && (
                        <div className="text-xs text-gray-500 italic">
                          📎 Media attached ({content.media.length} file{content.media.length > 1 ? 's' : ''})
                        </div>
                      )}
                      {content.actions && content.actions.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-2">
                          {content.actions.map((action, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                            >
                              {action.title || action.url || 'Button'}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Progress */}
            {progress && (
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-3">Sending Progress</h2>
                <div className="mb-2">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>{progress.current} / {progress.total}</span>
                    <span>{Math.round((progress.current / progress.total) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-green-500 h-3 rounded-full transition-all duration-200"
                      style={{ width: `${(progress.current / progress.total) * 100}%` }}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
                  <span className="text-green-600">✓ {progress.sent} sent</span>
                  {progress.skipped > 0 && <span className="text-yellow-600">⊘ {progress.skipped} opted-out</span>}
                  {progress.failed > 0 && <span className="text-red-600">✗ {progress.failed} failed</span>}
                </div>
              </div>
            )}

            {/* Results */}
            {results && (
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Send Results</h2>
                <div className="flex items-center gap-6 mb-4">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircleIcon className="h-5 w-5" />
                    <span className="font-medium">{results.sent} sent</span>
                  </div>
                  {results.skipped > 0 && (
                    <div className="flex items-center gap-2 text-yellow-600">
                      <ExclamationCircleIcon className="h-5 w-5" />
                      <span className="font-medium">{results.skipped} opted-out (skipped)</span>
                    </div>
                  )}
                  {results.failed > 0 && (
                    <div className="flex items-center gap-2 text-red-600">
                      <ExclamationCircleIcon className="h-5 w-5" />
                      <span className="font-medium">{results.failed} failed</span>
                    </div>
                  )}
                </div>
                {results.errors && results.errors.length > 0 && (
                  <div className="mt-3">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Errors:</h3>
                    <div className="max-h-40 overflow-y-auto bg-gray-50 rounded p-3 space-y-1">
                      {results.errors.map((err, idx) => (
                        <div key={idx} className="text-xs text-red-600">
                          <span className="font-mono">{err.phone}</span>: {err.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right column: Sender + Recipients */}
          <div className="space-y-6">
            {/* Sender number */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Sender Number</h2>
              {loadingSenders ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600" />
                </div>
              ) : senders.length === 0 ? (
                <div>
                  <p className="text-sm text-gray-500 mb-3">No provisioned numbers found.</p>
                  <input
                    type="text"
                    value={fromNumber}
                    onChange={(e) => setFromNumber(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-green-500 focus:border-green-500"
                    placeholder="+14155238886"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter a WhatsApp-enabled number manually (E.164)
                  </p>
                </div>
              ) : (
                <div>
                  <select
                    value={fromNumber}
                    onChange={(e) => setFromNumber(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Select a sender number...</option>
                    {senders.map(sender => (
                      <option key={sender.phone} value={sender.phone}>
                        {sender.phone}
                        {sender.displayName ? ` — ${sender.displayName}` : ''}
                        {sender.status !== 'ONLINE' ? ` [${sender.status}]` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    WhatsApp senders from your Twilio account
                  </p>
                </div>
              )}
            </div>

            {/* CSV Upload */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <ArrowUpTrayIcon className="h-5 w-5 text-green-600" />
                Recipients
              </h2>

              <label className="block cursor-pointer">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-400 transition-colors">
                  <ArrowUpTrayIcon className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-600">
                    {csvFileName || 'Upload CSV file'}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    phone,var1,var2,... (first col = number, rest = template variables)
                  </p>
                </div>
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleCsvUpload}
                  className="hidden"
                />
              </label>

              {recipients.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      {recipients.length} recipient{recipients.length > 1 ? 's' : ''} loaded
                    </span>
                    <button
                      onClick={clearRecipients}
                      className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                    >
                      <XMarkIcon className="h-3 w-3" />
                      Clear
                    </button>
                  </div>
                  <div className="max-h-48 overflow-y-auto bg-gray-50 rounded p-2 space-y-1">
                    {recipients.slice(0, 50).map((r, idx) => (
                      <div key={idx} className="text-xs font-mono text-gray-600">
                        {r.phone}
                        {Object.keys(r.variables).length > 0 && (
                          <span className="text-gray-400 ml-2">
                            {Object.values(r.variables).join(' | ')}
                          </span>
                        )}
                      </div>
                    ))}
                    {recipients.length > 50 && (
                      <div className="text-xs text-gray-400 pt-1">
                        ...and {recipients.length - 50} more
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Send Test */}
            {selectedTemplate && (
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-3">Send Test</h2>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={testNumber}
                    onChange={(e) => setTestNumber(e.target.value)}
                    placeholder="+972501234567"
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-green-500 focus:border-green-500"
                  />
                  <button
                    onClick={handleSendTest}
                    disabled={sendingTest || !fromNumber.trim() || !testNumber.trim()}
                    className="flex items-center gap-2 bg-yellow-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {sendingTest ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <PaperAirplaneIcon className="h-4 w-4" />
                    )}
                    Send Test
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-400">Send the selected template to a single number (no variables)</p>
              </div>
            )}

            {/* Send button */}
            <button
              onClick={handleSend}
              disabled={sending || !selectedTemplate || recipients.length === 0 || !fromNumber.trim()}
              className="w-full flex items-center justify-center gap-2 bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Sending...
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="h-5 w-5" />
                  Send Broadcast
                </>
              )}
            </button>

            {/* Opt-out list management */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-3">Unsubscribe List</h2>
              <p className="text-xs text-gray-500 mb-3">
                Numbers on this list will be automatically skipped during broadcast sends.
              </p>
              <div className="flex items-center gap-2 mb-4">
                <input
                  type="text"
                  value={optoutInput}
                  onChange={(e) => setOptoutInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddOptout()}
                  placeholder="+972501234567"
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-red-500 focus:border-red-500"
                />
                <button
                  onClick={handleAddOptout}
                  disabled={!optoutInput.trim()}
                  className="bg-red-500 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Add
                </button>
              </div>
              {loadingOptouts ? (
                <div className="text-xs text-gray-400">Loading...</div>
              ) : optouts.length === 0 ? (
                <div className="text-xs text-gray-400">No opted-out numbers yet.</div>
              ) : (
                <div className="max-h-48 overflow-y-auto bg-gray-50 rounded p-2 space-y-1">
                  {optouts.map((item) => (
                    <div key={item.phone} className="flex items-center justify-between text-xs py-1 px-2 bg-white rounded border border-gray-100">
                      <div>
                        <span className="font-mono text-gray-700">{item.phone}</span>
                        {item.reason && <span className="ml-2 text-gray-400">— {item.reason}</span>}
                      </div>
                      <button
                        onClick={() => handleRemoveOptout(item.phone)}
                        className="text-red-400 hover:text-red-600 ml-2"
                        title="Remove from opt-out list"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default WhatsAppBroadcast;
